// Notification Service for Push Notifications Management
import {
  fetchVapidPublicKey,
  registerVapidSubscription,
  unregisterVapidSubscription,
  fetchVapidPushStatus,
  sendVapidTestNotification,
} from '@vaiu/utils';
import { 
  initializeFirebase, 
  getFCMToken, 
  deleteFCMToken, 
  subscribeFCM, 
  unsubscribeFCM, 
  getFCMStatus,
  sendTestFCMNotification,
  setupForegroundMessageListener,
  clearFCMStatusCache,
  FCM_VAPID_KEY
} from './firebaseService';

export interface NotificationSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  image?: string;
  data?: any;
  tag?: string;
  actions?: NotificationAction[];
  requireInteraction?: boolean;
  silent?: boolean;
  timestamp?: number;
  renotify?: boolean;
  vibrate?: number[];
  dir?: 'auto' | 'ltr' | 'rtl';
  lang?: string;
}

export interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
}

export interface NotificationStatus {
  vapidSubscribed: boolean;
  fcmSubscribed: boolean;
  fcmEnabled: boolean;
  permission: NotificationPermission;
  hasServiceWorker: boolean;
  supportsPush: boolean;
}

// Cache interface for API responses
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class NotificationService {
  private swRegistration: ServiceWorkerRegistration | null = null;
  private vapidPublicKey: string = '';
  private isInitialized: boolean = false;
  private fcmToken: string | null = null;
  private fcmUnsubscriber: (() => void) | null = null;
  
  // Cache for API responses
  private apiCache: Map<string, CacheEntry<any>> = new Map();
  private readonly VAPID_CACHE_KEY = 'vapid_key';
  private readonly FCM_STATUS_CACHE_KEY = 'fcm_status';
  private readonly VAPID_STATUS_CACHE_KEY = 'vapid_status';
  private readonly VAPID_CACHE_DURATION = 60 * 60 * 1000; // 1 hour
  private readonly FCM_STATUS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // VAPID public key - must match Firebase Web Push certificates for the active project
  private readonly DEFAULT_VAPID_KEY = FCM_VAPID_KEY;

  constructor() {
    this.init();
  }

  // Initialize notification service
  async init(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Initialize Firebase
      await initializeFirebase();
      
      // Register service worker
      await this.registerServiceWorker();
      this.setupServiceWorkerSyncListener();

      // Don't fetch authenticated data during init to avoid API calls
      // It will be fetched when needed
      console.log('[Notifications] Service initialized successfully');
      this.isInitialized = true;
    } catch (error) {
      console.error('[Notifications] Failed to initialize service:', error);
      throw error;
    }
  }

  // Register service worker
  private async registerServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        this.swRegistration = registration;
        console.log('[Notifications] Service Worker registered successfully');
      } catch (error) {
        console.error('[Notifications] Service Worker registration failed:', error);
        throw error;
      }
    } else {
      console.warn('[Notifications] Service Worker not supported');
    }
  }

  // Fetch data that requires authentication (with caching)
  private async fetchAuthenticatedData(): Promise<void> {
    try {
      // Get VAPID key from backend or cache
      await this.fetchVapidKey();

      // Set up FCM foreground message listener (only needs to be done once)
      if (!this.fcmUnsubscriber) {
        this.setupFCMForegroundListener();
      }

      console.log('[Notifications] Authenticated data fetched successfully');
    } catch (error) {
      console.error('[Notifications] Failed to fetch authenticated data:', error);
      // Don't throw - use fallbacks
      this.vapidPublicKey = this.DEFAULT_VAPID_KEY;
    }
  }

  // Generic cache methods
  private getCachedData<T>(key: string): T | null {
    const cached = this.apiCache.get(key);
    if (!cached) return null;

    const now = Date.now();
    if (now > cached.expiresAt) {
      this.apiCache.delete(key);
      return null;
    }

    return cached.data;
  }

  private setCachedData<T>(key: string, data: T, duration: number): void {
    const now = Date.now();
    this.apiCache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + duration
    });
  }

  // Set up FCM foreground message listener
  private setupFCMForegroundListener(): void {
    try {
      this.fcmUnsubscriber = setupForegroundMessageListener((payload) => {
        console.log('[Notifications] FCM message received in foreground:', payload);
        
        // Show notification if app is in foreground
        if (payload.notification) {
          this.showLocalNotification({
            title: payload.notification.title || 'Notification',
            body: payload.notification.body || '',
            icon: payload.notification.icon,
            data: payload.data
          });
        }

        // Emit custom event for the app to handle
        window.dispatchEvent(new CustomEvent('fcmMessage', { detail: payload }));
      });
    } catch (error) {
      console.error('[Notifications] Failed to set up FCM foreground listener:', error);
    }
  }

  // Request notification permission
  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.error('[Notifications] This browser does not support desktop notifications');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      console.warn('[Notifications] Notification permission was denied');
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('[Notifications] Permission result:', permission);
      return permission;
    } catch (error) {
      console.error('[Notifications] Error requesting permission:', error);
      return 'denied';
    }
  }

  // Show local notification
  async showLocalNotification(payload: NotificationPayload): Promise<void> {
    try {
      if (Notification.permission !== 'granted') {
        console.warn('[Notifications] Cannot show notification - permission not granted');
        return;
      }

      const notification = new Notification(payload.title, {
        body: payload.body,
        icon: payload.icon || '/icon-192x192.png',
        badge: payload.badge || '/icon-192x192.png',
        data: payload.data,
        tag: payload.tag,
        requireInteraction: payload.requireInteraction,
        silent: payload.silent,
        dir: payload.dir,
        lang: payload.lang
      });

      // Handle notification click
      notification.onclick = () => {
        console.log('[Notifications] Notification clicked');
        window.focus();
        notification.close();
        
        // Emit custom event for the app to handle
        window.dispatchEvent(new CustomEvent('notificationClick', { detail: payload }));
      };

    } catch (error) {
      console.error('[Notifications] Failed to show notification:', error);
    }
  }

  private async ensureServiceWorkerReady(): Promise<void> {
    if (!this.swRegistration) {
      await this.registerServiceWorker();
    }

    if ('serviceWorker' in navigator) {
      await navigator.serviceWorker.ready;
    }
  }

  // Fetch VAPID key from backend (with caching)
  private setupServiceWorkerSyncListener(): void {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    navigator.serviceWorker.addEventListener('message', (event) => {
      const { type, subscription } = event.data || {};
      if (type === 'SW_PUSH_SUBSCRIPTION_SYNC_NEEDED' && subscription) {
        registerVapidSubscription(subscription).catch((error) => {
          console.error('[Notifications] Failed to sync push subscription from service worker:', error);
        });
      }
    });
  }

  private async fetchVapidKey(): Promise<void> {
    try {
      const cachedKey = this.getCachedData<string>(this.VAPID_CACHE_KEY);
      if (cachedKey) {
        this.vapidPublicKey = cachedKey;
        console.log('[Notifications] VAPID key retrieved from cache');
        return;
      }

      const publicKey = await fetchVapidPublicKey();
      if (publicKey) {
        this.vapidPublicKey = publicKey;
        this.setCachedData(this.VAPID_CACHE_KEY, publicKey, this.VAPID_CACHE_DURATION);
        console.log('[Notifications] VAPID key fetched from backend and cached');
        return;
      }

      console.warn('[Notifications] VAPID key endpoint unavailable, using bundled default');
      this.vapidPublicKey = this.DEFAULT_VAPID_KEY;
    } catch (error) {
      this.vapidPublicKey = this.DEFAULT_VAPID_KEY;
    }
  }

  // Subscribe to push notifications (both VAPID and FCM)
  async subscribe(): Promise<{ vapid: NotificationSubscription | null, fcm: string | null }> {
    const results = {
      vapid: null as NotificationSubscription | null,
      fcm: null as string | null
    };

    await this.ensureServiceWorkerReady();

    // Ensure authenticated data is fetched
    await this.fetchAuthenticatedData();

    // Request permission first
    const permission = await this.requestPermission();
    if (permission !== 'granted') {
      console.warn('[Notifications] Permission not granted');
      return results;
    }

    // Subscribe to VAPID notifications
    try {
      results.vapid = await this.subscribeVAPID();
    } catch (error) {
      console.error('[Notifications] VAPID subscription failed:', error);
    }

    // Subscribe to FCM notifications
    try {
      results.fcm = await this.subscribeFCMNotifications();
    } catch (error) {
      console.error('[Notifications] FCM subscription failed:', error);
    }

    return results;
  }

  // Subscribe to VAPID notifications
  async subscribeVAPID(): Promise<NotificationSubscription | null> {
    if (!this.swRegistration) {
      console.error('[Notifications] Service Worker not registered');
      return null;
    }

    try {
      // Check if already subscribed
      let subscription = await this.swRegistration.pushManager.getSubscription();
      
      if (!subscription) {
        // Create new subscription
        subscription = await this.swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
        });
      }

      const subscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
          auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
        }
      };

      // Send subscription to backend
      await this.sendVAPIDSubscriptionToBackend(subscriptionData);

      console.log('[Notifications] VAPID subscription successful');
      return subscriptionData;

    } catch (error) {
      console.error('[Notifications] Failed to subscribe to VAPID:', error);
      return null;
    }
  }

  // Subscribe to FCM notifications
  async subscribeFCMNotifications(): Promise<string | null> {
    try {
      const fcmStatus = await getFCMStatus();
      if (fcmStatus.enabled === false) {
        console.warn('[Notifications] FCM is not enabled on the platform');
        return null;
      }

      const token = await getFCMToken();
      if (!token) {
        console.warn('[Notifications] Failed to get FCM token');
        return null;
      }

      // Subscribe via backend
      const success = await subscribeFCM(token);
      if (success) {
        this.fcmToken = token;
        // Clear cache to force refresh of status
        this.setCachedData(this.FCM_STATUS_CACHE_KEY, { subscribed: true }, this.FCM_STATUS_CACHE_DURATION);
        clearFCMStatusCache();
        console.log('[Notifications] FCM subscription successful');
        return token;
      } else {
        console.error('[Notifications] FCM backend subscription failed');
        return null;
      }
    } catch (error) {
      console.error('[Notifications] Failed to subscribe to FCM:', error);
      return null;
    }
  }

  // Unsubscribe from push notifications (both VAPID and FCM)
  async unsubscribe(): Promise<{ vapid: boolean, fcm: boolean }> {
    const results = {
      vapid: false,
      fcm: false
    };

    // Unsubscribe from VAPID
    try {
      results.vapid = await this.unsubscribeVAPID();
    } catch (error) {
      console.error('[Notifications] VAPID unsubscription failed:', error);
    }

    // Unsubscribe from FCM
    try {
      results.fcm = await this.unsubscribeFCMNotifications();
    } catch (error) {
      console.error('[Notifications] FCM unsubscription failed:', error);
    }

    return results;
  }

  // Unsubscribe from VAPID notifications
  async unsubscribeVAPID(): Promise<boolean> {
    if (!this.swRegistration) {
      console.warn('[Notifications] Service Worker not registered');
      return false;
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();

      if (subscription) {
        const endpoint = subscription.endpoint;
        const backendRemoved = await unregisterVapidSubscription(endpoint);
        if (!backendRemoved) {
          console.warn('[Notifications] Backend VAPID unsubscription failed');
        }

        await subscription.unsubscribe();
        this.apiCache.delete(this.VAPID_STATUS_CACHE_KEY);
        console.log('[Notifications] VAPID unsubscription successful');
        return true;
      }

      console.log('[Notifications] No VAPID subscription found');
      return true;
    } catch (error) {
      console.error('[Notifications] Failed to unsubscribe from VAPID:', error);
      return false;
    }
  }

  // Unsubscribe from FCM notifications
  async unsubscribeFCMNotifications(): Promise<boolean> {
    try {
      const success = await unsubscribeFCM();
      if (success) {
        this.fcmToken = null;
        // Clear cache to force refresh of status
        this.setCachedData(this.FCM_STATUS_CACHE_KEY, { subscribed: false }, this.FCM_STATUS_CACHE_DURATION);
        clearFCMStatusCache();
        console.log('[Notifications] FCM unsubscription successful');
        return true;
      } else {
        console.error('[Notifications] FCM backend unsubscription failed');
        return false;
      }
    } catch (error) {
      console.error('[Notifications] Failed to unsubscribe from FCM:', error);
      return false;
    }
  }

  // Check if currently subscribed to VAPID
  async isVAPIDSubscribed(): Promise<boolean> {
    if (!this.swRegistration) return false;

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      return subscription !== null;
    } catch (error) {
      console.error('[Notifications] Failed to check VAPID subscription:', error);
      return false;
    }
  }

  // Check comprehensive subscription status (with caching for FCM status)
  async getStatus(): Promise<NotificationStatus> {
    const status: NotificationStatus = {
      vapidSubscribed: false,
      fcmSubscribed: false,
      fcmEnabled: true,
      permission: Notification.permission,
      hasServiceWorker: !!this.swRegistration,
      supportsPush: 'PushManager' in window
    };

    try {
      const cachedVapidStatus = this.getCachedData<{ subscribed: boolean }>(this.VAPID_STATUS_CACHE_KEY);
      if (cachedVapidStatus) {
        status.vapidSubscribed = cachedVapidStatus.subscribed;
      } else {
        const vapidStatus = await fetchVapidPushStatus();
        status.vapidSubscribed = vapidStatus.subscribed;
        this.setCachedData(this.VAPID_STATUS_CACHE_KEY, vapidStatus, this.FCM_STATUS_CACHE_DURATION);
      }
    } catch (error) {
      console.error('[Notifications] Failed to check VAPID status:', error);
      status.vapidSubscribed = await this.isVAPIDSubscribed();
    }

    try {
      const cachedStatus = this.getCachedData<any>(this.FCM_STATUS_CACHE_KEY);
      if (cachedStatus) {
        status.fcmSubscribed = cachedStatus.subscribed;
        status.fcmEnabled = cachedStatus.enabled !== false;
        console.log('[Notifications] FCM status retrieved from cache');
      } else {
        const fcmStatus = await getFCMStatus();
        status.fcmSubscribed = fcmStatus.subscribed;
        status.fcmEnabled = fcmStatus.enabled !== false;
        this.setCachedData(this.FCM_STATUS_CACHE_KEY, fcmStatus, this.FCM_STATUS_CACHE_DURATION);
        console.log('[Notifications] FCM status fetched from backend and cached');
      }
    } catch (error) {
      status.fcmSubscribed = false;
      status.fcmEnabled = false;
    }

    return status;
  }

  // Clear all notification caches (useful for debugging or force refresh)
  clearCache(): void {
    this.apiCache.clear();
    clearFCMStatusCache();
    console.log('[Notifications] All API caches cleared');
  }

  // Get current VAPID subscription
  async getVAPIDSubscription(): Promise<NotificationSubscription | null> {
    if (!this.swRegistration) return null;

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      
      if (subscription) {
        return {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
            auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
          }
        };
      }
      
      return null;
    } catch (error) {
      console.error('[Notifications] Failed to get VAPID subscription:', error);
      return null;
    }
  }

  // Send test notifications (both VAPID and FCM)
  async sendTestNotifications(): Promise<{ vapid: boolean, fcm: boolean }> {
    const results = {
      vapid: false,
      fcm: false
    };

    try {
      results.vapid = await sendVapidTestNotification();
    } catch (error) {
      console.error('[Notifications] VAPID test failed:', error);
    }

    try {
      results.fcm = await sendTestFCMNotification();
    } catch (error) {
      console.error('[Notifications] FCM test failed:', error);
    }

    return results;
  }

  // Cleanup resources
  destroy(): void {
    if (this.fcmUnsubscriber) {
      this.fcmUnsubscriber();
      this.fcmUnsubscriber = null;
    }
    
    this.clearCache();
    console.log('[Notifications] Service destroyed');
  }

  // Helper methods for encoding/decoding
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  // Send VAPID subscription to backend
  private async sendVAPIDSubscriptionToBackend(subscription: NotificationSubscription): Promise<void> {
    const success = await registerVapidSubscription(subscription);
    if (!success) {
      throw new Error('Push subscription API unavailable or invalid response');
    }
  }

  async refreshStatus(): Promise<NotificationStatus> {
    this.apiCache.delete(this.FCM_STATUS_CACHE_KEY);
    this.apiCache.delete(this.VAPID_STATUS_CACHE_KEY);
    clearFCMStatusCache();
    
    return await this.getStatus();
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService; 